/**
 * useLongPress — v1.4.30 unified long-press / right-click / context-key
 * detection hook. Returns props to spread onto ANY interactive element
 * (button, div with role="button", etc.) to receive a "context" event
 * from touch, mouse and TV-remote inputs alike.
 *
 * Usage:
 *
 *   const contextProps = useLongPress((clientX, clientY) => {
 *     setMenuOpen({ x: clientX, y: clientY });
 *   });
 *   <button {...contextProps} onClick={handleTap}>…</button>
 *
 * Detection:
 *   • Touch: pointerdown → wait 500 ms without pointer motion → fire.
 *   • Mouse: right-click (contextmenu event) fires it immediately.
 *   • Keyboard / TV remote: pressing the "ContextMenu" key
 *     (KEYCODE_MENU on Android TV) or long-press-Enter/OK also fires.
 *
 * The onClick is only cancelled when a long-press actually fired —
 * a short tap still triggers a normal click.
 */
import { useCallback, useEffect, useRef } from "react";

const LONG_PRESS_MS = 500;
const MOVE_THRESHOLD_PX = 10;

export default function useLongPress(onContext, { disabled = false } = {}) {
  const timerRef = useRef(null);
  const firedRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0 });
  const lastKeyDownAtRef = useRef(0);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => clear(), [clear]);

  const fire = useCallback(
    (x, y) => {
      firedRef.current = true;
      try {
        onContext?.(x, y);
      } catch {
        /* swallow — bad callback shouldn't crash the tile */
      }
    },
    [onContext],
  );

  const onPointerDown = useCallback(
    (e) => {
      if (disabled) return;
      firedRef.current = false;
      // Only left-button (0) starts the timer on mouse; touch / pen
      // always start (button is -1 on those on some engines).
      if (e.pointerType === "mouse" && e.button !== 0) return;
      startRef.current = { x: e.clientX, y: e.clientY };
      clear();
      timerRef.current = setTimeout(() => {
        fire(startRef.current.x, startRef.current.y);
      }, LONG_PRESS_MS);
    },
    [disabled, clear, fire],
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!timerRef.current) return;
      const dx = Math.abs(e.clientX - startRef.current.x);
      const dy = Math.abs(e.clientY - startRef.current.y);
      if (dx > MOVE_THRESHOLD_PX || dy > MOVE_THRESHOLD_PX) clear();
    },
    [clear],
  );

  const onPointerUp = useCallback(() => clear(), [clear]);
  const onPointerCancel = useCallback(() => clear(), [clear]);
  const onPointerLeave = useCallback(() => clear(), [clear]);

  const onContextMenu = useCallback(
    (e) => {
      if (disabled) return;
      // Right-click / long-press pop menu key on desktop.
      e.preventDefault();
      const x = e.clientX ?? startRef.current.x ?? 0;
      const y = e.clientY ?? startRef.current.y ?? 0;
      fire(x, y);
    },
    [disabled, fire],
  );

  const onKeyDown = useCallback(
    (e) => {
      if (disabled) return;
      // ContextMenu key (Android TV KEYCODE_MENU, some remotes).
      if (e.key === "ContextMenu") {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect?.();
        fire(rect ? rect.left + rect.width / 2 : 0, rect ? rect.top + rect.height / 2 : 0);
        return;
      }
      // Long-press Enter/OK on TV remotes (hold ≥ LONG_PRESS_MS).
      if (e.key === "Enter" || e.key === " ") {
        if (e.repeat) {
          // Chromium fires repeat events every ~30 ms while a key is held.
          const now = Date.now();
          if (!lastKeyDownAtRef.current) lastKeyDownAtRef.current = now;
          if (now - lastKeyDownAtRef.current >= LONG_PRESS_MS && !firedRef.current) {
            const rect = e.currentTarget.getBoundingClientRect?.();
            fire(
              rect ? rect.left + rect.width / 2 : 0,
              rect ? rect.top + rect.height / 2 : 0,
            );
          }
        } else {
          lastKeyDownAtRef.current = Date.now();
        }
      }
    },
    [disabled, fire],
  );

  const onKeyUp = useCallback((e) => {
    if (e.key === "Enter" || e.key === " ") lastKeyDownAtRef.current = 0;
  }, []);

  // Wrap onClick so a long-press doesn't ALSO trigger a normal click.
  const wrapOnClick = useCallback(
    (userOnClick) => (e) => {
      if (firedRef.current) {
        firedRef.current = false;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      userOnClick?.(e);
    },
    [],
  );

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onPointerLeave,
    onContextMenu,
    onKeyDown,
    onKeyUp,
    wrapOnClick,
    /**
     * Convenience for callers who only need to spread the pointer +
     * context handlers without touching onClick.
     */
    spread: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onPointerLeave,
      onContextMenu,
      onKeyDown,
      onKeyUp,
    },
  };
}
